/**
 * Chargement à la demande des polices fabric, pour les surfaces SANS éditeur (vignettes,
 * îlots, mockups) : une preview rend les layers tels quels — si la police n'est pas dans le
 * document CSS, canvas retombe en silence sur une police système.
 *
 * On ne charge que ce que les layers utilisent : le registre complet (~1,5 Ko de JSON, servi
 * par le layout) mappe famille -> variantes, et chaque famille n'est téléchargée qu'une fois
 * — dédup en page ici, cache HTTP entre les pages (woff2 CDN). Tout charger partout
 * coûterait ~15 requêtes par page ; à la demande, c'est zéro sur une bibliothèque sans
 * texte stylé.
 *
 * La dédup garde la PROMESSE de chaque variante : une vignette rendue pendant qu'une autre
 * télécharge la police attend le même chargement, au lieu de se rendre en police de repli.
 * Et une police arrivée fait oublier à Fabric les mesures prises sans elle (lib
 * forgetFontMeasures) — sinon elles faussent les textes de toute la page, éditeur compris.
 */
import { fontFamiliesOf, forgetFontMeasures } from "@frabr/fabric-editor";

type FontEntry = { family: string; url: string; weight?: string };

let registry: FontEntry[] | null = null;
const loading = new Map<string, Promise<void>>();

function fontRegistry(): FontEntry[] {
  if (registry) return registry;

  const node = document.querySelector("script[data-role='fabric-font-registry']");
  registry = node ? (Object.values(JSON.parse(node.textContent || "{}")) as FontEntry[]) : [];
  return registry;
}

/** Charge (une fois) toutes les variantes des familles que ces layers utilisent, styles par
 *  caractère compris (un mot dans une autre police). */
export async function ensureFontsForLayers(layers: object[]): Promise<void> {
  const families = new Set(fontFamiliesOf(layers));
  if (families.size === 0) return;

  const loads = fontRegistry()
    .filter((entry) => families.has(entry.family))
    .map((entry) => {
      const key = `${entry.family}/${entry.weight || "normal"}`;
      if (!loading.has(key)) loading.set(key, loadFace(entry));
      return loading.get(key);
    });
  await Promise.all(loads);
}

function loadFace(entry: FontEntry): Promise<void> {
  const face = new FontFace(entry.family, entry.url, { weight: entry.weight || "normal" });
  return face.load().then(
    (loaded) => {
      document.fonts.add(loaded);
      forgetFontMeasures(entry.family);
    },
    // Une police qui échoue ne bloque pas le rendu : fallback système, comme avant.
    () => {},
  );
}
